import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { isEmpty } from 'lodash'
import { closeModal } from '@/reducers/ui/ui.redux'
import {
  createPlaylist,
  fetchPlaylist,
  fetchPlaylists,
  patchPlaylist,
} from '@/reducers/playlists/playlists.actions'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { fetchUsers } from '@/reducers/organizations/organizationUsers.actions'
import { apiService, fetchingAPI } from '@/api'

export const playlistVisibilitySelectOptions = {
  public: {
    value: 'public',
    label: 'Public (Default)',
    isPublic: true,
    description:
      'Anyone in your organization will be able to see the name, contents and author of the playlist.',
  },
  private: {
    value: 'private',
    label: 'Private',
    isPublic: false,
    description:
      'Only you will be able to see this playlist and its contents. You can share this privately with other users in your organization.',
  },
}

export const CreateOrUpdatePlaylistButtonAndModal = ({
  action,
  organization,
  loading,
  buttonProps = {},
}) => {
  const dispatch = useDispatch()
  // used only for 'update'
  const { playlist } = useSelector((state) => state.playlists)
  const { currentlyOpenModalId } = useSelector((state) => state.ui)
  const { users } = useSelector((state) => state.organizationUsers)
  const { organizationid: currentUserOrgId, user_id: currentUserId } = useSelector(
    (state) => state.currentUser
  )

  const isBaltoAdmin = currentUserOrgId === 1
  const isBaltoAdminHandlingOtherOrgPlaylist = isBaltoAdmin && organization?.id !== 1
  const isBaltoAdminUpdatingOtherUserPlaylist =
    isBaltoAdmin && action === 'update' && playlist && playlist.user_id !== currentUserId

  const [isLoadingPlaylistOrgUsers, setIsLoadingPlaylistOrgUsers] = useState(false)
  const [playlistOrgUsers, setPlaylistOrgUsers] = useState([])
  const [visibleOnlyToMe, setVisibleOnlyToMe] = useState(false)
  const [allowedUsers, setAllowedUsers] = useState([])
  const [cacheAllowedUsers, setCacheAllowedUsers] = useState([])
  const [selectedVisibilityOption, setSelectedVisibilityOption] = useState(
    playlistVisibilitySelectOptions.public
  )

  const userOptions = useMemo(
    () =>
      playlistOrgUsers
        ?.filter((user) => {
          const isUserUpdatingPlaylist = action === 'update' && playlist

          if (isUserUpdatingPlaylist) return user.id !== playlist.user_id

          return user.id !== currentUserId
        })
        .map((user) => ({
          label: `${user.first_name} ${user.last_name}`,
          value: user.id,
        })),
    [playlistOrgUsers]
  )

  const setInitialStates = () => {
    if (action === 'create') {
      setVisibleOnlyToMe(false)
      setSelectedVisibilityOption(playlistVisibilitySelectOptions.public)
      setAllowedUsers([])
      setCacheAllowedUsers([])
    } else if (action === 'update' && playlist) {
      setVisibleOnlyToMe(!playlist.public_visibility && isEmpty(playlist.allowed_users))
      setSelectedVisibilityOption(
        playlist.public_visibility
          ? playlistVisibilitySelectOptions.public
          : playlistVisibilitySelectOptions.private
      )
      setAllowedUsers(
        playlist.public_visibility || isEmpty(playlist.allowed_users)
          ? []
          : userOptions
              .filter((option) => playlist.allowed_users.includes(option.value))
              .map((option) => option)
      )
      setCacheAllowedUsers([])
    }
  }

  useEffect(() => {
    setInitialStates()
  }, [currentlyOpenModalId, playlist, userOptions])

  useEffect(() => {
    if (organization && isEmpty(users)) dispatch(fetchUsers({ organizationId: organization.id }))
  }, [organization])

  useEffect(() => {
    if (organization && !isEmpty(users)) {
      if (isBaltoAdminHandlingOtherOrgPlaylist) {
        setIsLoadingPlaylistOrgUsers(true)
        // admin creating/updating playlist for/from other organization
        fetchingAPI(
          `${apiService.core}/api/organizations/${organization.id}/users`,
          'GET',
          dispatch
        )
          .then((res) => {
            setPlaylistOrgUsers(res.users)
          })
          .finally(() => {
            setIsLoadingPlaylistOrgUsers(false)
          })
      } else {
        setPlaylistOrgUsers(users)
      }
    }
  }, [users, organization])

  useEffect(() => {
    const isUsersDropdownDisabled = visibleOnlyToMe || selectedVisibilityOption.isPublic
    const shouldCacheSelectedUsers = allowedUsers.length > 0 && isUsersDropdownDisabled
    if (shouldCacheSelectedUsers) {
      setCacheAllowedUsers(allowedUsers)
      setAllowedUsers([])
      return
    }

    const shouldApplyUsersCache = cacheAllowedUsers.length > 0 && !isUsersDropdownDisabled
    if (shouldApplyUsersCache) {
      setAllowedUsers(cacheAllowedUsers)
      setCacheAllowedUsers([])
    }
  }, [visibleOnlyToMe, selectedVisibilityOption])

  if (!organization) {
    return null
  }
  if (!['create', 'update'].includes(action)) {
    return null
  }
  if (action === 'update' && !playlist) {
    return null
  }

  const handleClose = () => {
    dispatch(closeModal())
  }

  const handlePlaylistAction = async (formValues) => {
    if (action === 'create') {
      const created = await dispatch(
        createPlaylist({
          name: formValues.name,
          organizationId: organization.id,
          visibility: visibleOnlyToMe ? false : selectedVisibilityOption.isPublic,
          allowedUsers:
            visibleOnlyToMe || selectedVisibilityOption.isPublic
              ? []
              : allowedUsers.map((user) => user.value),
        })
      )

      if (!created) {
        return
      }
    } else {
      const values = {}
      values.public_visibility = visibleOnlyToMe ? false : selectedVisibilityOption.isPublic
      values.allowed_users =
        visibleOnlyToMe || selectedVisibilityOption.isPublic
          ? []
          : allowedUsers.map((user) => user.value)
      if (formValues.name) values.name = formValues.name

      await dispatch(patchPlaylist(playlist.uuid, organization.id, values))
      await dispatch(fetchPlaylist(playlist.uuid, organization.id))
      dispatch(fetchPlaylists(organization.id))
    }
    handleClose()
  }

  const playlistActionSchema = [
    {
      name: 'name',
      type: 'text',
      label: 'Playlist Name',
      required: action === 'create',
      autoFocus: true,
    },
    {
      name: 'visibleOnlyToMe',
      type: 'checkbox',
      label: isBaltoAdminUpdatingOtherUserPlaylist
        ? 'Visible Only To Author'
        : 'Visible Only to Me',
      checked: visibleOnlyToMe,
      onClick: (e) => {
        const val = e.target.checked
        setVisibleOnlyToMe(val)
      },
    },
    {
      name: 'visibility',
      type: 'select',
      label: 'Visibility',
      disabled: visibleOnlyToMe,
      value: visibleOnlyToMe ? playlistVisibilitySelectOptions.private : selectedVisibilityOption,
      onChange: (selectedOption) => {
        setSelectedVisibilityOption(selectedOption)
      },
      isSearchable: false,
      isClearable: false,
      autoFocus: false,
      options: [playlistVisibilitySelectOptions.public, playlistVisibilitySelectOptions.private],
    },
    {
      name: 'allowedUsers',
      type: 'multiSelect',
      label: 'Also visible to',
      loading: isLoadingPlaylistOrgUsers,
      isDisabled: visibleOnlyToMe || selectedVisibilityOption.isPublic,
      'data-value': JSON.stringify(allowedUsers),
      value: allowedUsers,
      onChange: (value) => {
        if (Array.isArray(value)) {
          setAllowedUsers(value)
        }
      },
      options: userOptions,
    },
  ]

  return (
    <ButtonAndFormModal
      buttonLabel={action === 'create' ? 'Create Playlist' : 'Manage Playlist'}
      modalTitle={
        action === 'create'
          ? `Create Playlist for ${organization.name}`
          : `Update Playlist from ${organization.name}`
      }
      modalId={action === 'create' ? 'playlists/create' : `playlist/edit/${playlist.uuid}`}
      modalProps={{ size: 'tiny' }}
      buttonProps={{
        primary: action === 'create',
        loading,
        ...buttonProps,
      }}
      disabled={
        action === 'update' && (loading || (!isBaltoAdmin && playlist.user_id !== currentUserId))
      }
      form={
        <AbstractForm
          initialValues={{
            name: action === 'create' ? '' : playlist.name,
            visibleOnlyToMe,
          }}
          schema={playlistActionSchema}
          onSubmit={handlePlaylistAction}
          buttonLabel={action === 'create' ? 'Save' : 'Update'}
          onClose={handleClose}
          isModal
        />
      }
    />
  )
}
